import type {ReactElement} from 'react';
import {useEffect, useState} from 'react';
import {SkeletonBrick} from '@Components/skeleton-brick/skeleton-brick';
import type {SelectedImageAsset, SelectedImageAssetPart} from '@Components/ai-chat-window/ai-chat-window.types';
import {getUrlForGeneratedAiChatModelAssetsImage} from '@Libraries/ai-chat.library';
import {getUserImageThumbUrl} from '@Components/ai-chat-window/lib/chat-attachment-image.library';
import {getPosterThumbnailImagePreviewURL} from '@Libraries/poster-library';
import {getAssetUrl as getStaticAssetUrl} from '@Utils/s3.util';
import {MediaItemSizeType} from '@Libraries/s3-library';
import styles from './selected-asset-thumb.module.scss';
import {SELECTED_ASSET_THUMB_SIZE_PX} from './selected-asset-thumb.types';
import type {SelectedAssetThumbProps} from './selected-asset-thumb.types';

const ASYNC_RESOLVED_TYPES: ReadonlySet<string> = new Set(['design']);

const unwrapPart = (part: SelectedImageAssetPart): SelectedImageAssetPart => {
  return part.image === undefined ? part : unwrapPart(part.image);
};

const getInitialThumbUrl = (asset: SelectedImageAsset): string | null => {
  const part = unwrapPart(asset.imagePart);
  switch (part.type) {
    case 'user_image':
      if (part.hashedFilename === undefined || part.extension === undefined) {
        return null;
      }
      return getUserImageThumbUrl(part.hashedFilename, part.extension);
    case 'image':
    case 'reattached_model_image':
      return part.hashedId === undefined ? null : getUrlForGeneratedAiChatModelAssetsImage(part.hashedId, MediaItemSizeType.THUMB);
    case 'example_image_asset':
      return part.fileName === undefined ? null : getStaticAssetUrl(`${part.fileName}-thumb`);
    case 'url_image':
    case 'image_graphic_item_snapshot':
      return part.url ?? null;
    case 'design':
      return null;
    default:
      return null;
  }
};

const designAsyncResolution = async (part: SelectedImageAssetPart): Promise<string> => {
  if (part.hashedId === undefined) {
    return '';
  }
  return getPosterThumbnailImagePreviewURL(part.hashedId, part.pageHashedId ?? undefined);
};

export function SelectedAssetThumb({asset}: SelectedAssetThumbProps): ReactElement {
  const [thumbUrl, setThumbUrl] = useState<string | null>(() => getInitialThumbUrl(asset));
  const [hasError, setHasError] = useState(() => isUnresolvable(asset));

  useEffect(() => {
    setHasError(isUnresolvable(asset));
    setThumbUrl(getInitialThumbUrl(asset));

    const part = unwrapPart(asset.imagePart);
    if (part.type !== 'design') {
      return;
    }

    void (async (): Promise<void> => {
      const url = await designAsyncResolution(part);
      if (url.length === 0) {
        setHasError(true);
        return;
      }
      setThumbUrl(url);
    })();
  }, [asset]);

  if (hasError) {
    return <div className={`radius-8 ${styles.errorPlaceholder}`}>{'Failed to load image'}</div>;
  }

  if (thumbUrl === null) {
    return <SkeletonBrick className={'radius-8'} width={SELECTED_ASSET_THUMB_SIZE_PX} height={SELECTED_ASSET_THUMB_SIZE_PX} />;
  }

  return (
    <a href={thumbUrl} target="_blank" rel="noreferrer">
      <img
        src={thumbUrl}
        alt=""
        aria-hidden="true"
        className={`radius-8 ${styles.thumb}`}
        onError={(): void => {
          setHasError(true);
        }}
      />
    </a>
  );
}

function isUnresolvable(asset: SelectedImageAsset): boolean {
  return getInitialThumbUrl(asset) === null && !ASYNC_RESOLVED_TYPES.has(unwrapPart(asset.imagePart).type);
}
